import React, { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer'
import { Box, Typography, IconButton, Button } from '@mui/material'
import { Fullscreen, FullscreenExit } from '@mui/icons-material'
import './Design3DViewer.css'

import { Splitter, Sash } from './DesignCanvas'
import { ComponentItem } from './ComponentLibrary'

interface Design3DViewerProps {
    width: number
    height: number
    depth?: number
    frameWidth?: number
    themeMode?: 'light' | 'dark'
    splitters?: Splitter[]
    sashes?: Sash[]
    hybridMaterials?: { core: string; cladding: string; interior: string; hardware: string }
    components?: ComponentItem[]
    componentLibraryOverlay?: React.ReactNode
    surfaceMaterial?: string
}

const Design3DViewer: React.FC<Design3DViewerProps> = ({ width, height, depth = 50, frameWidth = 60, themeMode = 'light', splitters = [], sashes = [], hybridMaterials, components = [], componentLibraryOverlay, surfaceMaterial = 'ClearGlass' }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const mountRef = useRef<HTMLDivElement>(null)
    const sceneRef = useRef<THREE.Scene | null>(null)
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)
    const animationFrameRef = useRef<number>()
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showComponentLibrary, setShowComponentLibrary] = useState(false)

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    useEffect(() => {
        if (!mountRef.current) return

        // Scene setup
        const scene = new THREE.Scene()
        // Dark Mode: Dark Slate (#121212), Light Mode: Light Gray (#f0f0f0)
        scene.background = new THREE.Color(themeMode === 'dark' ? 0x1e1e1e : 0xf0f0f0)
        sceneRef.current = scene

        // Camera setup
        const maxDim = Math.max(width, height, 1000)
        const camera = new THREE.PerspectiveCamera(
            50, // 50 FOV is better for product viewing (less distortion)
            mountRef.current.clientWidth / mountRef.current.clientHeight,
            1,
            maxDim * 10 // Dynamic far plane so large windows don't clip
        )
        // Position camera directly in front to fit the object
        camera.position.set(0, 0, maxDim * 1.5)
        camera.lookAt(0, 0, 0)
        cameraRef.current = camera

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({ antialias: true })
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight)
        renderer.shadowMap.enabled = true
        renderer.localClippingEnabled = true
        mountRef.current.appendChild(renderer.domElement)
        rendererRef.current = renderer

        // CSS2D Renderer for labels
        const labelRenderer = new CSS2DRenderer()
        labelRenderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight)
        labelRenderer.domElement.style.position = 'absolute'
        labelRenderer.domElement.style.top = '0px'
        labelRenderer.domElement.style.pointerEvents = 'none'
        mountRef.current.appendChild(labelRenderer.domElement)

        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.7)
        scene.add(ambientLight)

        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6)
        directionalLight.position.set(200, 400, 500)
        directionalLight.castShadow = true
        scene.add(directionalLight)

        const fillLight = new THREE.DirectionalLight(0xffffff, 0.3)
        fillLight.position.set(-200, -100, 300)
        scene.add(fillLight)

        const baseFrameMaterial = new THREE.MeshStandardMaterial({
            color: 0x3a3c3f, // Anthracite Grey
            roughness: 0.8,
            metalness: 0.1
        })
        
        let frameMaterial: THREE.Material | THREE.Material[] = baseFrameMaterial;
        let hardwareMatColor = 0x222222;
        let hardwareRoughness = 0.4;
        let hardwareMetalness = 0.8;

        if (hybridMaterials) {
            const coreMaterial = new THREE.MeshStandardMaterial({ color: 0xf0f0f0, roughness: 0.9 }); // PVC
            const claddingMaterial = new THREE.MeshStandardMaterial({ color: 0x2b2d30, roughness: 0.4, metalness: 0.6 }); // Aluminum
            const interiorMaterial = new THREE.MeshStandardMaterial({ color: 0x8b5a2b, roughness: 0.7 }); // Wood
            // BoxGeometry materials: right, left, top, bottom, front, back
            frameMaterial = [coreMaterial, coreMaterial, coreMaterial, coreMaterial, claddingMaterial, interiorMaterial];
            
            hardwareMatColor = 0xd0d0d0; // Brushed steel
            hardwareRoughness = 0.3;
            hardwareMetalness = 0.9;
        }

        let gColor = 0xeef2f5;
        let gOpacity = 0.85;
        let gRoughness = 0.6;
        let gMetalness = 0.1;

        if (surfaceMaterial === 'ClearGlass') {
            gColor = 0xd4e6f1; gOpacity = 0.3; gRoughness = 0.1; gMetalness = 0.2;
        } else if (surfaceMaterial === 'FrostedGlass') {
            gColor = 0xffffff; gOpacity = 0.8; gRoughness = 0.9;
        } else if (surfaceMaterial === 'TintedBronze') {
            gColor = 0xcd7f32; gOpacity = 0.5; gRoughness = 0.1;
        } else if (surfaceMaterial === 'TintedBlue') {
            gColor = 0x4682b4; gOpacity = 0.5; gRoughness = 0.1;
        } else if (surfaceMaterial === 'ResinClear') {
            gColor = 0xf0f8ff; gOpacity = 0.4; gRoughness = 0.3;
        } else if (surfaceMaterial === 'ResinOpaque') {
            gColor = 0xffffff; gOpacity = 1.0; gRoughness = 0.5;
        } else if (surfaceMaterial === 'SolidPanel') {
            gColor = 0x3a3c3f; // dark grey
            gOpacity = 1.0; gRoughness = 0.8;
        }

        const isTransparent = gOpacity < 1.0;
        const glassMaterial = new THREE.MeshPhysicalMaterial({
            color: gColor,
            transparent: isTransparent,
            opacity: isTransparent ? 1.0 : gOpacity,
            roughness: gRoughness,
            metalness: gMetalness,
            transmission: isTransparent ? 1.0 - gOpacity : 0,
            ior: 1.5,
            thickness: 4,
            side: THREE.DoubleSide,
            depthWrite: !isTransparent
        })
        const hardwareMaterial = new THREE.MeshStandardMaterial({
            color: hardwareMatColor,
            roughness: hardwareRoughness,
            metalness: hardwareMetalness
        })

        const frameGroup = new THREE.Group()
        const animatables: THREE.Object3D[] = []

        // Create frame geometry out of 4 rails (hollow frame)
        // Top and Bottom rails (full width)
        const tbGeometry = new THREE.BoxGeometry(width, frameWidth, depth)
        const topRail = new THREE.Mesh(tbGeometry, frameMaterial)
        topRail.position.set(0, height / 2 - frameWidth / 2, 0)
        topRail.castShadow = true
        topRail.receiveShadow = true
        frameGroup.add(topRail)

        const bottomRail = new THREE.Mesh(tbGeometry, frameMaterial)
        bottomRail.position.set(0, -height / 2 + frameWidth / 2, 0)
        bottomRail.castShadow = true
        bottomRail.receiveShadow = true
        frameGroup.add(bottomRail)

        // Left and Right rails (between top and bottom)
        const sideHeight = height - 2 * frameWidth
        const lrGeometry = new THREE.BoxGeometry(frameWidth, Math.max(0, sideHeight), depth)
        
        const leftRail = new THREE.Mesh(lrGeometry, frameMaterial)
        leftRail.position.set(-width / 2 + frameWidth / 2, 0, 0)
        leftRail.castShadow = true
        leftRail.receiveShadow = true
        frameGroup.add(leftRail)

        const rightRail = new THREE.Mesh(lrGeometry, frameMaterial)
        rightRail.position.set(width / 2 - frameWidth / 2, 0, 0)
        rightRail.castShadow = true
        rightRail.receiveShadow = true
        frameGroup.add(rightRail)

        // Splitters
        splitters.forEach(splitter => {
            if (splitter.type === 'vertical') {
                const xPos = -width / 2 + (width * splitter.position / 100)
                const vSplitterGeo = new THREE.BoxGeometry(frameWidth, Math.max(0, sideHeight), depth)
                const mesh = new THREE.Mesh(vSplitterGeo, frameMaterial)
                mesh.position.set(xPos, 0, 0)
                mesh.castShadow = true
                mesh.receiveShadow = true
                frameGroup.add(mesh)
            } else {
                const yPos = height / 2 - (height * splitter.position / 100)
                const hWidth = width - 2 * frameWidth
                const hSplitterGeo = new THREE.BoxGeometry(hWidth, frameWidth, depth)
                const mesh = new THREE.Mesh(hSplitterGeo, frameMaterial)
                mesh.position.set(0, yPos, 0)
                mesh.castShadow = true
                mesh.receiveShadow = true
                frameGroup.add(mesh)
            }
        })

        // Sashes and Glass
        if (sashes.length > 0) {
            sashes.forEach(sash => {
                const sashCenterX = -width/2 + sash.x + sash.width/2
                const sashCenterY = height/2 - sash.y - sash.height/2
                
                const sashDepth = depth * 0.8
                const sashFrameWidth = frameWidth * 0.8
                
                const hingeGroup = new THREE.Group()
                
                let hX = sashCenterX;
                let hY = sashCenterY;
                if (sash.type === 'casement' || sash.type === 'opening' || sash.type === 'accordion') {
                    hX = sashCenterX - sash.width/2;
                } else if (sash.type === 'tilt') {
                    hY = sashCenterY - sash.height/2;
                }
                
                hingeGroup.position.set(hX, hY, 0)
                hingeGroup.userData = { 
                    animatable: sash.type !== 'fixed', 
                    type: 'sash', 
                    sashType: sash.type, 
                    isOpen: false, 
                    targetVal: 0,
                    targetZ: 0,
                    initialX: hX,
                    initialZ: 0,
                    sashWidth: sash.width
                }
                animatables.push(hingeGroup)

                const sashGroup = new THREE.Group()
                // Position sashGroup relative to hinge
                if (sash.type === 'casement' || sash.type === 'opening' || sash.type === 'accordion') {
                    sashGroup.position.set(sash.width/2, 0, 0)
                } else if (sash.type === 'tilt') {
                    sashGroup.position.set(0, sash.height/2, 0)
                } else {
                    sashGroup.position.set(0, 0, 0)
                }
                
                const sTbGeo = new THREE.BoxGeometry(sash.width, sashFrameWidth, sashDepth)
                const sTop = new THREE.Mesh(sTbGeo, frameMaterial)
                sTop.position.set(0, sash.height/2 - sashFrameWidth/2, 0)
                const sBot = new THREE.Mesh(sTbGeo, frameMaterial)
                sBot.position.set(0, -sash.height/2 + sashFrameWidth/2, 0)
                
                const sSideH = sash.height - 2 * sashFrameWidth
                const sLrGeo = new THREE.BoxGeometry(sashFrameWidth, Math.max(0, sSideH), sashDepth)
                const sLeft = new THREE.Mesh(sLrGeo, frameMaterial)
                sLeft.position.set(-sash.width/2 + sashFrameWidth/2, 0, 0)
                const sRight = new THREE.Mesh(sLrGeo, frameMaterial)
                sRight.position.set(sash.width/2 - sashFrameWidth/2, 0, 0)
                
                sashGroup.add(sTop, sBot, sLeft, sRight)
                
                const sGlassW = Math.max(1, sash.width - 2 * sashFrameWidth)
                const sGlassH = Math.max(1, sash.height - 2 * sashFrameWidth)
                const sGlassGeo = new THREE.BoxGeometry(sGlassW, sGlassH, 4)
                const sGlassMesh = new THREE.Mesh(sGlassGeo, glassMaterial)
                sashGroup.add(sGlassMesh)

                if (sash.type !== 'fixed') {
                    const handleBaseGeo = new THREE.BoxGeometry(10, 50, 10)
                    const handleBase = new THREE.Mesh(handleBaseGeo, hardwareMaterial)
                    handleBase.position.set(sash.width/2 - sashFrameWidth/2, 0, sashDepth/2 + 5)
                    
                    const handleBarGeo = new THREE.BoxGeometry(8, 40, 20)
                    const handleBar = new THREE.Mesh(handleBarGeo, hardwareMaterial)
                    handleBar.position.set(sash.width/2 - sashFrameWidth/2, -10, sashDepth/2 + 15)
                    
                    sashGroup.add(handleBase, handleBar)
                }

                hingeGroup.add(sashGroup)
                frameGroup.add(hingeGroup)
            })
        } else {
            const glassW = Math.max(1, width - 2 * frameWidth)
            const glassH = Math.max(1, height - 2 * frameWidth)
            const glassGeometry = new THREE.BoxGeometry(glassW, glassH, 4)
            const globalGlass = new THREE.Mesh(glassGeometry, glassMaterial)
            frameGroup.add(globalGlass)
        }

        // Components rendering
        if (components && components.length > 0) {
            const hasRoller = components.some(c => c.category === 'roller');
            if (hasRoller) {
                const rollerHeight = 150; // mm
                const rollerDepth = depth + 20;
                const rollerGeo = new THREE.BoxGeometry(width + 20, rollerHeight, rollerDepth);
                const rollerMat = new THREE.MeshStandardMaterial({ color: 0xd0d0d0, roughness: 0.8 });
                const rollerMesh = new THREE.Mesh(rollerGeo, rollerMat);
                // frameGroup is centered at 0,0,0. Top is height/2.
                rollerMesh.position.set(0, height / 2 + rollerHeight / 2, 0);
                rollerMesh.castShadow = true;
                rollerMesh.receiveShadow = true;
                frameGroup.add(rollerMesh);
            }

            const hasScreen = components.some(c => c.category === 'screen');
            if (hasScreen) {
                const screenGeo = new THREE.BoxGeometry(width - frameWidth, height - frameWidth, 2);
                const screenMat = new THREE.MeshStandardMaterial({ 
                    color: 0x333333, 
                    transparent: true, 
                    opacity: 0.3, 
                    wireframe: true // gives a mesh look
                });
                const screenMesh = new THREE.Mesh(screenGeo, screenMat);
                screenMesh.position.set(0, 0, depth / 2 + 5);
                screenMesh.userData = { animatable: true, type: 'screen', isOpen: false, targetY: 0, initialY: 0, openY: height }
                animatables.push(screenMesh)
                frameGroup.add(screenMesh);
            }

            const hasShutter = components.some(c => c.category === 'shutter' || c.category === 'roller');
            if (hasShutter) {
                // Draw closed slats
                const slatHeight = 40;
                const innerHeight = height - 2 * frameWidth;
                const innerWidth = width - 2 * frameWidth;
                const numSlats = Math.floor(innerHeight / slatHeight);
                const shutterGroup = new THREE.Group();
                
                // Hide slats that roll up above the top of the frame
                const clippingPlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), height / 2);
                const slatGeo = new THREE.BoxGeometry(innerWidth, slatHeight - 2, 10);
                const slatMat = new THREE.MeshStandardMaterial({ 
                    color: 0xcccccc, 
                    roughness: 0.7,
                    clippingPlanes: [clippingPlane]
                });
                
                for (let i = 0; i < numSlats; i++) {
                    const slat = new THREE.Mesh(slatGeo, slatMat);
                    // Start from top to bottom
                    const yPos = innerHeight / 2 - (i * slatHeight) - slatHeight / 2;
                    slat.position.set(0, yPos, depth / 2 + 15);
                    // slight rotation for slats
                    slat.rotation.x = -0.1;
                    shutterGroup.add(slat);
                }
                shutterGroup.userData = { animatable: true, type: 'shutter', isOpen: false, targetY: 0, initialY: 0, openY: height }
                animatables.push(shutterGroup)
                frameGroup.add(shutterGroup);
            }
        }

        scene.add(frameGroup)

        // Labels and Helpers removed for a clean catalog look

        // Add OrbitControls
        const controls = new OrbitControls(camera, renderer.domElement)
        controls.enableDamping = true
        controls.dampingFactor = 0.05
        controls.screenSpacePanning = true
        controls.minDistance = 50
        controls.maxDistance = maxDim * 5 // Allow zooming out further for large items

        // Raycaster for click events
        const raycaster = new THREE.Raycaster()
        const mouse = new THREE.Vector2()
        
        const onClick = (event: MouseEvent) => {
            if (!rendererRef.current || !cameraRef.current || !sceneRef.current) return
            
            const rect = rendererRef.current.domElement.getBoundingClientRect()
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
            
            raycaster.setFromCamera(mouse, cameraRef.current)
            const intersects = raycaster.intersectObjects(sceneRef.current.children, true)
            
            if (intersects.length > 0) {
                let object: any = intersects[0].object
                while (object && !object.userData.animatable) {
                    object = object.parent
                }
                
                if (object && object.userData.animatable) {
                    object.userData.isOpen = !object.userData.isOpen
                    if (object.userData.type === 'sash') {
                        const isOpen = object.userData.isOpen;
                        const sType = object.userData.sashType;
                        if (sType === 'casement' || sType === 'opening') {
                            object.userData.targetVal = isOpen ? Math.PI / 2 : 0;
                        } else if (sType === 'tilt') {
                            object.userData.targetVal = isOpen ? -Math.PI / 8 : 0;
                        } else if (sType === 'sliding') {
                            object.userData.targetVal = isOpen ? object.userData.sashWidth * 0.9 : 0;
                        } else if (sType === 'lift_sliding') {
                            object.userData.targetVal = isOpen ? object.userData.sashWidth * 0.9 : 0;
                            object.userData.targetZ = isOpen ? 20 : 0;
                        } else if (sType === 'pivot') {
                            object.userData.targetVal = isOpen ? Math.PI / 2.5 : 0;
                        } else if (sType === 'accordion') {
                            object.userData.targetVal = isOpen ? Math.PI / 1.2 : 0;
                        }
                    } else if (object.userData.type === 'screen' || object.userData.type === 'shutter') {
                        object.userData.targetY = object.userData.isOpen ? object.userData.openY : object.userData.initialY
                    }
                }
            }
        }
        renderer.domElement.addEventListener('click', onClick)

        // Animation loop
        const animate = () => {
            animationFrameRef.current = requestAnimationFrame(animate)

            controls.update() // required if controls.enableDamping is true
            
            animatables.forEach(obj => {
                if (obj.userData.type === 'sash') {
                    const sType = obj.userData.sashType;
                    const val = obj.userData.targetVal;
                    if (sType === 'casement' || sType === 'opening' || sType === 'accordion') {
                        obj.rotation.y = THREE.MathUtils.lerp(obj.rotation.y, val, 0.1);
                    } else if (sType === 'tilt') {
                        obj.rotation.x = THREE.MathUtils.lerp(obj.rotation.x, val, 0.1);
                    } else if (sType === 'sliding') {
                        obj.position.x = THREE.MathUtils.lerp(obj.position.x, obj.userData.initialX + val, 0.1);
                    } else if (sType === 'lift_sliding') {
                        obj.position.x = THREE.MathUtils.lerp(obj.position.x, obj.userData.initialX + val, 0.1);
                        obj.position.z = THREE.MathUtils.lerp(obj.position.z, obj.userData.initialZ + obj.userData.targetZ, 0.1);
                    } else if (sType === 'pivot') {
                        obj.rotation.y = THREE.MathUtils.lerp(obj.rotation.y, val, 0.1);
                    }
                } else if (obj.userData.type === 'screen' || obj.userData.type === 'shutter') {
                    obj.position.y = THREE.MathUtils.lerp(obj.position.y, obj.userData.targetY, 0.1)
                }
            })

            renderer.render(scene, camera)
            labelRenderer.render(scene, camera)
        }
        animate()

        // Handle resize
        const handleResize = () => {
            if (!mountRef.current || !camera || !renderer) return

            camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight
            camera.updateProjectionMatrix()
            renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight)
            labelRenderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight)
        }
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
            if (rendererRef.current && rendererRef.current.domElement) {
                rendererRef.current.domElement.removeEventListener('click', onClick)
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current)
            }
            if (mountRef.current && renderer.domElement && mountRef.current.contains(renderer.domElement)) {
                mountRef.current.removeChild(renderer.domElement)
            }
            if (mountRef.current && labelRenderer.domElement && mountRef.current.contains(labelRenderer.domElement)) {
                mountRef.current.removeChild(labelRenderer.domElement)
            }
            controls.dispose()
            renderer.dispose()
        }
    }, [width, height, depth, themeMode, splitters, sashes, hybridMaterials, components, surfaceMaterial])

    return (
        <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper', color: 'text.primary', position: 'relative' }}>
            {isFullscreen && showComponentLibrary && componentLibraryOverlay && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 60, 
                    right: 16, 
                    width: 400, 
                    maxHeight: 'calc(100vh - 80px)', 
                    overflowY: 'auto',
                    bgcolor: 'background.paper', 
                    boxShadow: 4, 
                    borderRadius: 1, 
                    zIndex: 1000,
                    p: 2
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>Components</div>
                        <IconButton size="small" onClick={() => setShowComponentLibrary(false)}>
                            <span style={{fontSize: '0.8rem', lineHeight: 1}}>✖</span>
                        </IconButton>
                    </Box>
                    {componentLibraryOverlay}
                </Box>
            )}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight="600">3D Preview</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {width}mm × {height}mm × {depth}mm
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {isFullscreen && componentLibraryOverlay && (
                        <Button
                            variant={showComponentLibrary ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setShowComponentLibrary(!showComponentLibrary)}
                            startIcon={<span style={{fontSize: '1.2em'}}>📦</span>}
                        >
                            Components
                        </Button>
                    )}
                    <IconButton size="small" onClick={toggleFullscreen}>
                        {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                    </IconButton>
                </Box>
            </Box>
            <Box ref={mountRef} className="three-canvas-container" sx={{ position: 'relative', flexGrow: 1, bgcolor: themeMode === 'dark' ? '#1e1e1e' : '#f0f0f0' }} />
        </Box>
    )
}

export default Design3DViewer
